// src/hooks/queries/useProyectos.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { proyectosService } from '@/services/proyectos';
import type {
  ProyectoFiltros,
  ProyectoCreate,
  ProyectoUpdate,
  ProyectoFaseCreate,
  ProyectoFaseUpdate,
  ProyectoSprintCreate,
  ProyectoSprintUpdate,
  ProyectoMiembroCreate,
  ProyectoMiembroUpdate,
  ProjectStatus,
} from '@/types';

// ==================== PROYECTOS ====================

export function useProyectos(params?: ProyectoFiltros) {
  return useQuery({
    queryKey: ['proyectos', params],
    queryFn: () => proyectosService.list(params),
  });
}

export function useProyecto(id: string) {
  return useQuery({
    queryKey: ['proyectos', id],
    queryFn: () => proyectosService.get(id),
    enabled: !!id,
  });
}

export function useProyectoResumen(id: string) {
  return useQuery({
    queryKey: ['proyectos', id, 'resumen'],
    queryFn: () => proyectosService.getResumen(id),
    enabled: !!id,
  });
}

export function useCrearProyecto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProyectoCreate) => proyectosService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proyectos'] });
    },
  });
}

export function useActualizarProyecto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProyectoUpdate }) =>
      proyectosService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', id] });
    },
  });
}

export function useEliminarProyecto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => proyectosService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proyectos'] });
    },
  });
}

export function useCambiarEstadoProyecto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: ProjectStatus }) =>
      proyectosService.cambiarEstado(id, estado),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', id] });
    },
  });
}

// ==================== FASES ====================

export function useFases(proyectoId: string) {
  return useQuery({
    queryKey: ['proyectos', proyectoId, 'fases'],
    queryFn: () => proyectosService.getFases(proyectoId),
    enabled: !!proyectoId,
  });
}

export function useFase(faseId: string) {
  return useQuery({
    queryKey: ['fases', faseId],
    queryFn: () => proyectosService.getFase(faseId),
    enabled: !!faseId,
  });
}

export function useCrearFase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proyectoId, data }: { proyectoId: string; data: ProyectoFaseCreate }) =>
      proyectosService.createFase(proyectoId, data),
    onSuccess: (_, { proyectoId }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'fases'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId] });
    },
  });
}

export function useActualizarFase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { faseId: string; data: ProyectoFaseUpdate; proyectoId: string }) =>
      proyectosService.updateFase(params.faseId, params.data),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'fases'] });
    },
  });
}

export function useEliminarFase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { faseId: string; proyectoId: string }) =>
      proyectosService.deleteFase(params.faseId),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'fases'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId] });
    },
  });
}

export function useReordenarFases() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proyectoId, orden }: { proyectoId: string; orden: string[] }) =>
      proyectosService.reordenarFases(proyectoId, orden),
    onSuccess: (_, { proyectoId }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'fases'] });
    },
  });
}

// ==================== SPRINTS ====================

export function useSprints(proyectoId: string) {
  return useQuery({
    queryKey: ['proyectos', proyectoId, 'sprints'],
    queryFn: () => proyectosService.getSprints(proyectoId),
    enabled: !!proyectoId,
  });
}

export function useSprint(sprintId: string) {
  return useQuery({
    queryKey: ['sprints', sprintId],
    queryFn: () => proyectosService.getSprint(sprintId),
    enabled: !!sprintId,
  });
}

export function useSprintActivo(proyectoId: string) {
  return useQuery({
    queryKey: ['proyectos', proyectoId, 'sprint-activo'],
    queryFn: () => proyectosService.getSprintActivo(proyectoId),
    enabled: !!proyectoId,
  });
}

export function useCrearSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proyectoId, data }: { proyectoId: string; data: ProyectoSprintCreate }) =>
      proyectosService.createSprint(proyectoId, data),
    onSuccess: (_, { proyectoId }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'sprints'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId] });
    },
  });
}

export function useActualizarSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; data: ProyectoSprintUpdate; proyectoId: string }) =>
      proyectosService.updateSprint(params.sprintId, params.data),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprints'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprint-activo'] });
    },
  });
}

export function useEliminarSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; proyectoId: string }) =>
      proyectosService.deleteSprint(params.sprintId),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprints'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId] });
    },
  });
}

export function useActivarSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; proyectoId: string }) =>
      proyectosService.activarSprint(params.sprintId),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprints'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprint-activo'] });
    },
  });
}

export function useCompletarSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { sprintId: string; proyectoId: string }) =>
      proyectosService.completarSprint(params.sprintId),
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprints'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', params.proyectoId, 'sprint-activo'] });
    },
  });
}

// ==================== MIEMBROS ====================

export function useMiembros(proyectoId: string) {
  return useQuery({
    queryKey: ['proyectos', proyectoId, 'miembros'],
    queryFn: () => proyectosService.getMiembros(proyectoId),
    enabled: !!proyectoId,
  });
}

export function useAgregarMiembro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proyectoId, data }: { proyectoId: string; data: ProyectoMiembroCreate }) =>
      proyectosService.addMiembro(proyectoId, data),
    onSuccess: (_, { proyectoId }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'miembros'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId] });
    },
  });
}

export function useActualizarRolMiembro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proyectoId, usuarioId, data }: { proyectoId: string; usuarioId: string; data: ProyectoMiembroUpdate }) =>
      proyectosService.updateMiembroRol(proyectoId, usuarioId, data),
    onSuccess: (_, { proyectoId }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'miembros'] });
    },
  });
}

export function useRemoverMiembro() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proyectoId, usuarioId }: { proyectoId: string; usuarioId: string }) =>
      proyectosService.removeMiembro(proyectoId, usuarioId),
    onSuccess: (_, { proyectoId }) => {
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId, 'miembros'] });
      queryClient.invalidateQueries({ queryKey: ['proyectos', proyectoId] });
    },
  });
}
